import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  fill, PROFILE_FACT_LABELS, PROFILE_FACT_VALUE_MAX, Size, educationDraftProblem, profileFactProblem, heldEducationLine, linkableSource, personEducationPath, personEducationRowPath, personProfileFactPath, personProfilePath,
  spanWords, t, type PersonEducationRow, type PersonEducationView, type PersonProfileFactRow, type PersonProfileView,
} from '@bms2/shared';
import { ConfirmationDialog, Pill, TextInput } from '../../components/controls';
import { LoadFailure } from '../../components/composites';
import { TileRowList, type TileItem, type TileRowLink } from '../../components/composites/Tile';
import { Icon } from '../../components/Icon';
import { useTileListRows } from '../../shell/ViewerZone';
import { TagChip } from './TagSwatch';
import { FormFrame } from '../../components/forms/FormFrame';
import { del, patch, post, useApi } from './api';
import styles from './PersonHistoryPanels.module.css';

/**
 * 🔒 The Person page's Education, Other Skills and Profile tiles (piece 13;
 * decision 6180; T2872). Each reads its own route, so the record page's one
 * answer stays the size it was. A row a validation check's Accept wrote here
 * is one a person can take off (decision 5462). 🔨 A LinkedIn page is drawn as
 * a link opened in a new tab, and nothing here fetches it (decision 5381).
 * 🔒 Education and Profile draw the Employments tile's dated rows through the
 * Tile's own list, the pencil and the remove on each row, and Education's
 * + Add School is the tile head's action (decision 6400).
 */

const CHANGED = 'bms2:person-history';

/** 🔒 The pills one Show more adds to Other Skills: a fixed four, however many the first rows held (T2933). */
const SKILLS_PER_MORE = 4;

/** Tells every tile of the person to read again: an Accept on the Suggestions tile writes into them. */
export function announcePersonHistory(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(CHANGED));
}

function useHeard(reload: () => void): void {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.addEventListener(CHANGED, reload);
    return () => window.removeEventListener(CHANGED, reload);
  }, [reload]);
}

/** The address a LinkedIn page or a website opens on, or null where it is not one. */
function linkOf(value: string | null): string | null {
  if (!value) return null;
  return linkableSource(/^https?:\/\//i.test(value) ? value : `https://${value}`);
}

/** A remove a person has pressed and not yet confirmed, and the row it names. */
interface Removing<R> { row: R; name: string }

/**
 * 🔒 The remove's question, as the row's Remove asked it before the row took the
 * Tile's own controls (decision 6400): the row is gone from the person, and a
 * later check can suggest it again.
 */
function RemoveQuestion<R>({ removing, onConfirm, onCancel }: { removing: Removing<R> | null; onConfirm: (row: R) => void; onCancel: () => void }) {
  if (!removing) return null;
  return (
    <ConfirmationDialog
      open
      title={t('personHistory.removeTitle')}
      consequence={{ body: t('personHistory.removeConsequence'), recordNames: [removing.name] }}
      confirmLabel={t('personHistory.remove')}
      onConfirm={() => onConfirm(removing.row)}
      onCancel={onCancel}
    />
  );
}

/** The outside page a row names, drawn as the row's reach and opened in its own tab. */
function reachOf(href: string | null, label: string): TileRowLink | null {
  return href ? { href, label, icon: 'webPage', newTab: true } : null;
}

/**
 * 🔒 Education: the schools like the employments, the newest first (decision 6180).
 * 🔒 Each school is a dated row of the Employments tile's shape, the pencil and the
 * remove on it for a writer. + Add School is the tile head's action, and its window
 * is the record page's own, so a folded tile, which draws no panel, still opens it
 * (decision 6400); a save there is heard here through `announcePersonHistory`.
 */
export function PersonEducationPanel({ personId, apiBaseUrl = '' }: { personId: string; apiBaseUrl?: string }) {
  const fetched = useApi<PersonEducationView>(personEducationPath(personId), apiBaseUrl);
  useHeard(fetched.reload);
  const [editing, setEditing] = useState<PersonEducationRow | null>(null);
  const [removing, setRemoving] = useState<Removing<PersonEducationRow> | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  if (fetched.state.kind === 'loading') return <p className={styles.quiet}>{t('async.loading')}</p>;
  if (fetched.state.kind === 'failed') return <LoadFailure state={fetched.state} onRetry={fetched.reload} />;
  if (fetched.state.kind !== 'ready') return null;
  const view = fetched.state.data;
  const remove = async (row: PersonEducationRow) => {
    setRemoving(null);
    setBusy(row.id);
    setRefusal(null);
    const done = await del(personEducationRowPath(personId, row.id), apiBaseUrl);
    setBusy(null);
    if (!done.ok) setRefusal(done.message);
    fetched.reload();
  };
  const items: TileItem[] = view.rows.map((row) => ({
    type: 'datedRole',
    role: row.school,
    organization: [row.degree, row.fieldOfStudy].filter(Boolean).join(', ') || null,
    span: spanWords(row.startMonth, row.endMonth) || null,
    link: reachOf(linkOf(row.schoolLinkedInUrl), t('personHistory.education.field.linkedIn')),
    data: { 'data-education-row': row.id },
    ...(view.writes ? {
      onEdit: () => setEditing(row),
      editLabel: t('personHistory.education.modal.editTitle'),
      onDelete: busy === row.id ? null : () => setRemoving({ row, name: row.school }),
      deleteLabel: t('personHistory.remove'),
    } : {}),
  }));
  return (
    <div className={styles.listPanel} data-person-education={personId} data-tile-custom-flush="">
      {refusal ? <p className={`${styles.refusal} ${styles.said}`} role="alert">{refusal}</p> : null}
      {items.length === 0 ? <p className={`${styles.quiet} ${styles.said}`}>{t('personHistory.education.empty')}</p> : <TileRowList items={items} name="education" />}
      {editing ? (
        <EducationModal
          key={editing.id}
          personId={personId}
          initial={editing}
          apiBaseUrl={apiBaseUrl}
          onCancel={() => setEditing(null)}
          onSaved={fetched.reload}
        />
      ) : null}
      <RemoveQuestion removing={removing} onConfirm={(row) => void remove(row)} onCancel={() => setRemoving(null)} />
    </div>
  );
}

/**
 * 🔒 + Add School and its Edit (decision 6180): the school, the degree, the
 * field, the two months and the school's LinkedIn page. An edit names the
 * version it read. 🔨 Mounted only while open and keyed to the row, so a
 * second row never opens on the first one's draft.
 */
export function EducationModal({ personId, initial, apiBaseUrl = '', onCancel, onSaved }: {
  personId: string;
  initial: PersonEducationRow | null;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [school, setSchool] = useState(initial?.school ?? '');
  const [degree, setDegree] = useState(initial?.degree ?? '');
  const [fieldOfStudy, setField] = useState(initial?.fieldOfStudy ?? '');
  const [startMonth, setStart] = useState(initial?.startMonth ?? '');
  const [endMonth, setEnd] = useState(initial?.endMonth ?? '');
  const [linkedIn, setLinkedIn] = useState(initial?.schoolLinkedInUrl ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const draft = { school, degree, fieldOfStudy, startMonth, endMonth, schoolLinkedInUrl: linkedIn };
  const save = async () => {
    const problem = educationDraftProblem(draft);
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    const answer = initial ? await patch(personEducationRowPath(personId, initial.id), { ...draft, version: initial.version }, apiBaseUrl) : await post(personEducationPath(personId), draft, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setError(answer.message);
    onSaved();
    onCancel();
  };
  const held = { school: initial?.school ?? '', degree: initial?.degree ?? '', fieldOfStudy: initial?.fieldOfStudy ?? '', startMonth: initial?.startMonth ?? '', endMonth: initial?.endMonth ?? '', schoolLinkedInUrl: initial?.schoolLinkedInUrl ?? '' };
  const typed = (Object.keys(held) as (keyof typeof held)[]).some((key) => draft[key] !== held[key]);
  return (
    <FormFrame
      host="window"
      open
      title={initial ? t('personHistory.education.modal.editTitle') : t('personHistory.education.modal.addTitle')}
      subtitle={initial ? heldEducationLine(initial) : t('personHistory.education.modal.subtitle')}
      onCancel={onCancel}
      dataForm={initial ? 'edit-education' : 'add-education'}
      typed={typed}
      refusal={error}
      onDismissRefusal={() => setError(null)}
      act={{ label: initial ? t('personHistory.education.saveEdit') : t('personHistory.education.save'), onAct: save, disabled: busy || school.trim() === '', disabledReason: t('personHistory.education.field.school') }}
    >
      <div data-education-modal={initial ? 'edit' : 'add'}>
        <TextInput size={Size.Large} label={t('personHistory.education.field.school')} value={school} onChange={setSchool} required name="education-school" />
        <TextInput size={Size.Medium} label={t('personHistory.education.field.degree')} value={degree} onChange={setDegree} name="education-degree" />
        <TextInput size={Size.Medium} label={t('personHistory.education.field.fieldOfStudy')} value={fieldOfStudy} onChange={setField} name="education-field" />
        <TextInput size={Size.Small} label={t('personHistory.education.field.startMonth')} value={startMonth} onChange={setStart} name="education-start" />
        <TextInput size={Size.Small} label={t('personHistory.education.field.endMonth')} value={endMonth} onChange={setEnd} name="education-end" />
        <p className={styles.note}>{t('personHistory.education.field.monthHint')}</p>
        <TextInput size={Size.Large} label={t('personHistory.education.field.linkedIn')} value={linkedIn} onChange={setLinkedIn} name="education-linkedin" />
      </div>
    </FormFrame>
  );
}

/** The facts of one tile, read from the person's profile route. */
function useFacts(personId: string, apiBaseUrl: string) {
  const fetched = useApi<PersonProfileView>(personProfilePath(personId), apiBaseUrl);
  useHeard(fetched.reload);
  const [busy, setBusy] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const remove = async (row: PersonProfileFactRow) => {
    setBusy(row.id);
    setRefusal(null);
    const done = await del(personProfileFactPath(personId, row.id), apiBaseUrl);
    setBusy(null);
    if (!done.ok) setRefusal(done.message);
    fetched.reload();
  };
  return { fetched, busy, refusal, remove };
}

/**
 * 🔒 Other Skills: the skills an Accept kept outside the catalog, each a tag pill with its own ×; nothing
 * searches people by them (the owner, 2026-10-20). 🔨 The pills wrap, and the tile draws the Organization's
 * Tile list rows of them, one wrapped row of pills for one list row (decision 5960), then *N shown of M*,
 * Show more and Show fewer. A skill carries no note.
 */
export function PersonOtherSkillsPanel({ personId, apiBaseUrl = '' }: { personId: string; apiBaseUrl?: string }) {
  const { fetched, busy, refusal, remove } = useFacts(personId, apiBaseUrl);
  const listRows = useTileListRows();
  // T2921: the pages of pills drawn, one more for each Show more and the first again for Show fewer, as the Tile pages (decision 5960).
  const [pages, setPages] = useState(1);
  // The count of pills that fit the first rows; null while the pills are drawn whole to be measured.
  const [fit, setFit] = useState<number | null>(null);
  const [measuring, setMeasuring] = useState(true);
  const [removing, setRemoving] = useState<Removing<PersonProfileFactRow> | null>(null);
  const pills = useRef<HTMLUListElement | null>(null);
  const ready = fetched.state.kind === 'ready';
  const total = fetched.state.kind === 'ready' ? fetched.state.data.facts.filter((row) => row.kind === 'ListedSkill').length : 0;
  // T2921: a removed skill changes what the first rows hold, so the cut is measured again when the count or the rows move.
  const [measuredFor, setMeasuredFor] = useState(`${total}:${listRows}`);
  if (measuredFor !== `${total}:${listRows}`) {
    setMeasuredFor(`${total}:${listRows}`);
    setMeasuring(true);
  }
  useLayoutEffect(() => {
    if (!measuring) return;
    const list = pills.current;
    if (!list) return;
    let rowsSeen = 0;
    let lastTop: number | null = null;
    let count = list.children.length;
    for (let i = 0; i < list.children.length; i += 1) {
      const top = (list.children[i] as HTMLElement).offsetTop;
      if (lastTop === null || top !== lastTop) {
        rowsSeen += 1;
        lastTop = top;
        if (rowsSeen > listRows) { count = i; break; }
      }
    }
    setFit(count);
    setMeasuring(false);
  }, [measuring, listRows, total, ready]);
  useEffect(() => {
    const list = pills.current;
    if (!list || typeof ResizeObserver === 'undefined') return undefined;
    let width = list.clientWidth;
    const watcher = new ResizeObserver(() => {
      if (list.clientWidth !== width) {
        width = list.clientWidth;
        setMeasuring(true);
      }
    });
    watcher.observe(list);
    return () => watcher.disconnect();
  }, [ready, total]);
  if (fetched.state.kind === 'loading') return <p className={styles.quiet}>{t('async.loading')}</p>;
  if (fetched.state.kind === 'failed') return <LoadFailure state={fetched.state} onRetry={fetched.reload} />;
  if (fetched.state.kind !== 'ready') return null;
  const view = fetched.state.data;
  const rows = view.facts.filter((row) => row.kind === 'ListedSkill');
  const limit = measuring || fit === null ? rows.length : Math.min(fit + SKILLS_PER_MORE * (pages - 1), rows.length);
  const shown = rows.slice(0, limit);
  const paged = !measuring && fit !== null && fit < rows.length;
  const hidden = rows.length - shown.length;
  return (
    <div className={styles.panel} data-person-other-skills={personId}>
      <p className={styles.note}>{t('personHistory.skills.note')}</p>
      {refusal ? <p className={styles.refusal} role="alert">{refusal}</p> : null}
      {rows.length === 0 ? <p className={styles.quiet}>{t('personHistory.skills.empty')}</p> : (
        <ul ref={pills} className={styles.pills}>
          {shown.map((row) => (
            <li key={row.id} className={styles.pill} data-other-skill={row.id}>
              <TagChip label={row.value} size={Size.Medium} />
              {view.writes ? (
                <button type="button" className={styles.pillRemove} aria-label={fill(t('personHistory.skills.remove'), { skill: row.value })} disabled={busy === row.id} onClick={() => setRemoving({ row, name: row.value })} data-other-skill-remove={row.id}>
                  <Icon meaning="remove" size="inline" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {paged ? (
        <div className={styles.pager} data-other-skills-pager={personId}>
          <span className={styles.pagerCount}>{[shown.length, t('tile.shownOf'), rows.length].join(' ')}</span>
          {hidden > 0 ? <button type="button" className={styles.pagerAction} onClick={() => setPages((was) => was + 1)} data-other-skills-page="more">{t('tile.showMore')}</button> : null}
          {pages > 1 ? <button type="button" className={styles.pagerAction} onClick={() => setPages(1)} data-other-skills-page="fewer">{t('tile.showFewer')}</button> : null}
        </div>
      ) : null}
      <RemoveQuestion removing={removing} onConfirm={(row) => { setRemoving(null); void remove(row); }} onCancel={() => setRemoving(null)} />
    </div>
  );
}

/**
 * 🔒 Profile: the person's LinkedIn page as a link, and each assumed value with its
 * Assumed pill (decision 6180). 🔒 Each is a dated row of the Employments tile's
 * shape with the pencil and the remove on it for a writer (decision 6661); nothing
 * adds a fact here but a check's Accept, so the tile head carries no Add (decision 6400).
 */
export function PersonProfilePanel({ personId, apiBaseUrl = '' }: { personId: string; apiBaseUrl?: string }) {
  const { fetched, busy, refusal, remove } = useFacts(personId, apiBaseUrl);
  const [removing, setRemoving] = useState<Removing<PersonProfileFactRow> | null>(null);
  const [editing, setEditing] = useState<PersonProfileFactRow | null>(null);
  if (fetched.state.kind === 'loading') return <p className={styles.quiet}>{t('async.loading')}</p>;
  if (fetched.state.kind === 'failed') return <LoadFailure state={fetched.state} onRetry={fetched.reload} />;
  if (fetched.state.kind !== 'ready') return null;
  const view = fetched.state.data;
  const rows = view.facts.filter((row) => row.kind !== 'ListedSkill');
  const items: TileItem[] = rows.map((row) => ({
    type: 'datedRole',
    role: PROFILE_FACT_LABELS[row.kind],
    organization: row.value,
    pill: row.assumed ? { text: t('recordChecks.assumed'), group: 'inactive' } : null,
    link: row.kind === 'LinkedInPage' ? reachOf(linkOf(row.value), t('personHistory.profile.linkedIn')) : null,
    data: { 'data-profile-fact': row.kind },
    ...(view.writes ? {
      onEdit: busy === row.id ? null : () => setEditing(row),
      editLabel: t('personHistory.profile.edit'),
      onDelete: busy === row.id ? null : () => setRemoving({ row, name: [PROFILE_FACT_LABELS[row.kind], row.value].join(' · ') }),
      deleteLabel: t('personHistory.remove'),
    } : {}),
  }));
  return (
    <div className={styles.listPanel} data-person-profile={personId} data-tile-custom-flush="">
      {refusal ? <p className={`${styles.refusal} ${styles.said}`} role="alert">{refusal}</p> : null}
      {items.length === 0 ? <p className={`${styles.quiet} ${styles.said}`}>{t('personHistory.profile.empty')}</p> : <TileRowList items={items} name="profile" />}
      {editing ? <ProfileFactModal key={editing.id} personId={personId} row={editing} apiBaseUrl={apiBaseUrl} onCancel={() => setEditing(null)} onSaved={fetched.reload} /> : null}
      <RemoveQuestion removing={removing} onConfirm={(row) => { setRemoving(null); void remove(row); }} onCancel={() => setRemoving(null)} />
    </div>
  );
}

/**
 * 🔒 A Profile row's pencil (decision 6661): its value in a small window, Enter or
 * Save writing it and Escape or Cancel leaving the row as it was. The write names
 * the value the window opened on, so a second person's correction is refused
 * rather than overwritten. 🔨 Mounted only while open and keyed to the row.
 */
export function ProfileFactModal({ personId, row, apiBaseUrl = '', onCancel, onSaved }: {
  personId: string;
  row: PersonProfileFactRow;
  apiBaseUrl?: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(row.value);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const typed = value !== row.value;
  const save = async () => {
    if (busy) return;
    if (!typed) return onCancel();
    const problem = profileFactProblem(row.kind, value);
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    const answer = await patch(personProfileFactPath(personId, row.id), { value, was: row.value }, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setError(answer.message);
    onSaved();
    onCancel();
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('personHistory.profile.edit')}
      subtitle={t('personHistory.profile.editSubtitle')}
      onCancel={onCancel}
      dataForm="edit-profile-fact"
      typed={typed}
      refusal={error}
      onDismissRefusal={() => setError(null)}
      act={{ label: t('personHistory.profile.saveEdit'), onAct: save, disabled: busy || value.trim() === '', disabledReason: t('personHistory.profile.value') }}
    >
      {/* 🔒 Enter in the field saves, as the window's Save does (decision 6661). */}
      <div data-profile-fact-modal={row.kind} onKeyDown={(event) => { if (event.key === 'Enter' && !event.nativeEvent.isComposing) { event.preventDefault(); void save(); } }}>
        <TextInput size={Size.Large} label={PROFILE_FACT_LABELS[row.kind]} value={value} onChange={setValue} required maxLength={PROFILE_FACT_VALUE_MAX} name="profile-fact-value" />
      </div>
    </FormFrame>
  );
}

/** 🔒 The label every assumed value carries (decision 6180). */
export function AssumedPill() {
  return <Pill size={Size.Small} text={t('recordChecks.assumed')} foreground="var(--status-inactive-text)" background="var(--status-inactive-bg)" outlined />;
}
